/**
 * /mobility · the Mobility pillar surface.
 *
 * One front-door for every way a member moves beyond the airline ground
 * floor. Top: live commercial flights via Amadeus (with sort + filters
 * + Google Flights deeplink + pricing disclaimer). Below: 6 editorial
 * tiles for the high-touch transport modes — Private Aviation, Yacht,
 * Cruise, Rail, Chauffeured, Sea & Water — each routing a structured
 * brief through the concierge via the Aurelion dispatcher. Cruise and
 * Chauffeured tiles also surface live Viator inventory inside the brief.
 *
 * Founder ruling 28 Jun 2026: "Flights ARE mobility. One pillar, one
 * URL — flights at the top, everything else as tiles below."
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Today's ISO date (YYYY-MM-DD) · used as the `min` attribute on every
// date input across the page so members can't accidentally back-date
// a booking. Computed once at module load · refreshed on page reload.
const TODAY_ISO = new Date().toISOString().slice(0, 10);

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

// ── Cloudinary brand assets ──
const CLD = "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1200/lc/mobility";

const TILES = [
  {
    code: "aviation",
    label: "Private Aviation",
    blurb: "Private jets and helicopter transfers · NetJets · VistaJet · Air Charter Service · Blade.",
    photo: `${CLD}/aviation`,
  },
  {
    code: "yacht",
    label: "Yacht Charter",
    blurb: "Super-yacht weekly charters · Med, Caribbean, Maldives · day-yachts curated by your concierge.",
    photo: `${CLD}/yacht`,
  },
  {
    code: "cruise",
    label: "Cruise & Shore Excursions",
    blurb: "Silversea · Regent · Crystal · Explora · Ponant · Viking · Seabourn · live Viator shore excursions for major ports.",
    photo: `${CLD}/cruise`,
  },
  {
    code: "rail",
    label: "Luxury Rail",
    blurb: "Belmond · Venice-Simplon Orient Express · Maharajas&apos; · Rocky Mountaineer · Glacier Express · Rovos Rail.",
    photo: `${CLD}/rail`,
  },
  {
    code: "chauffeured",
    label: "Chauffeured Car",
    blurb: "Multi-day Bentley / Mercedes / Range Rover with vetted private driver · live Viator inventory per city.",
    photo: `${CLD}/chauffeured`,
  },
  {
    code: "sea_transfer",
    label: "Sea & Water Transfers",
    blurb: "Maldives seaplane · Venice water taxi · Hong Kong-Macau ferry · Capri tender · Riva sunset run.",
    photo: `${CLD}/sea-transfer`,
  },
];


export default function MobilityPage() {
  const [origin,      setOrigin]      = useState("Mumbai");
  const [destination, setDestination] = useState("Dubai");
  const [departDate,  setDepartDate]  = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [returnDate,  setReturnDate]  = useState("");
  const [adults,      setAdults]      = useState(1);
  const [cabin,       setCabin]       = useState("ECONOMY");

  const [offers,      setOffers]      = useState([]);
  const [gflights,    setGflights]    = useState("");
  const [disclaimer,  setDisclaimer]  = useState("");
  const [loading,     setLoading]     = useState(false);
  const [searched,    setSearched]    = useState(false);

  // Filters & sort
  const [sortBy,        setSortBy]        = useState("price_asc");
  const [stopsFilter,   setStopsFilter]   = useState("any");      // "any" | "nonstop"
  const [carrierFilter, setCarrierFilter] = useState(null);
  const [timeFilter,    setTimeFilter]    = useState("any");
  const [maxPrice,      setMaxPrice]      = useState("");
  const [maxDurationH,  setMaxDurationH]  = useState("");         // hours · max journey duration
  const [allianceFilter,setAllianceFilter]= useState(null);       // "Star" | "Oneworld" | "SkyTeam" | null
  const [premiumHubsOnly, setPremiumHubsOnly] = useState(false);

  const PREMIUM_HUBS = ["DXB","DOH","LHR","SIN","CDG","JFK","HKG","FRA","AMS","ZRH","IST","AUH"];

  const [activeBrief, setActiveBrief] = useState(null);

  async function searchFlights() {
    setLoading(true);
    setSearched(false);
    setOffers([]);
    setCarrierFilter(null);
    setStopsFilter("any");
    setTimeFilter("any");
    setMaxPrice("");
    setMaxDurationH("");
    setAllianceFilter(null);
    setPremiumHubsOnly(false);
    try {
      const params = new URLSearchParams({
        origin, destination,
        depart_date: departDate,
        adults: String(adults),
        cabin,
        limit: "50",
      });
      if (returnDate) params.set("return_date", returnDate);
      if (stopsFilter === "nonstop") params.set("nonstop", "true");
      const r = await fetch(`${API}/api/mobility/flights/search?${params}`);
      const j = await r.json();
      setOffers(j.offers || []);
      setGflights(j.google_flights_url || "");
      setDisclaimer(j.pricing_disclaimer || "");
    } catch {
      setOffers([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }

  // Apply filters + sort to the offers
  const filteredOffers = useMemo(() => {
    let list = [...offers];
    if (stopsFilter === "nonstop") list = list.filter(o => (o.stops || 0) === 0);
    if (carrierFilter) list = list.filter(o => (o.carriers || []).includes(carrierFilter));
    if (timeFilter !== "any") {
      list = list.filter(o => {
        const t = o.depart_at ? new Date(o.depart_at).getHours() : -1;
        if (t < 0) return false;
        if (timeFilter === "morning")    return t >= 5 && t < 12;
        if (timeFilter === "afternoon")  return t >= 12 && t < 17;
        if (timeFilter === "evening")    return t >= 17 && t < 21;
        if (timeFilter === "night")      return t >= 21 || t < 5;
        return true;
      });
    }
    if (maxPrice && Number(maxPrice) > 0) {
      list = list.filter(o => Number(o.total) <= Number(maxPrice));
    }
    if (maxDurationH && Number(maxDurationH) > 0) {
      list = list.filter(o => (o.duration_min || durationToMin(o.duration)) <= Number(maxDurationH) * 60);
    }
    if (allianceFilter) {
      list = list.filter(o => (o.alliances || []).includes(allianceFilter));
    }
    if (premiumHubsOnly) {
      list = list.filter(o => {
        const lvs = o.layovers || [];
        if (lvs.length === 0) return true;   // non-stop always counts as "premium" (no layover at all)
        return lvs.every(l => PREMIUM_HUBS.includes(l.iata));
      });
    }
    const cmp = {
      price_asc:    (a, b) => Number(a.total) - Number(b.total),
      price_desc:   (a, b) => Number(b.total) - Number(a.total),
      duration_asc: (a, b) => durationToMin(a.duration) - durationToMin(b.duration),
      depart_asc:   (a, b) => (a.depart_at || "").localeCompare(b.depart_at || ""),
    }[sortBy] || ((a, b) => 0);
    return list.sort(cmp);
  }, [offers, stopsFilter, carrierFilter, timeFilter, maxPrice, maxDurationH, allianceFilter, premiumHubsOnly, sortBy]);

  // Distinct carriers across the result-set, for the chip filter
  const carrierChips = useMemo(() => {
    const map = {};
    for (const o of offers) {
      const code = (o.carriers || [])[0];
      const name = (o.carrier_names || [])[0] || code;
      if (code && !map[code]) map[code] = name;
    }
    return Object.entries(map).map(([code, name]) => ({ code, name }));
  }, [offers]);

  // ── Compose a human-readable summary of currently-active filters so
  //    the concierge sees the EXACT context the member had set when
  //    they hit "Concierge® handles". Empty string when defaults.
  function activeFilterSummary() {
    const bits = [];
    if (stopsFilter === "nonstop")  bits.push("Non-stop only");
    if (carrierFilter) {
      const carrier = carrierChips.find(c => c.code === carrierFilter);
      bits.push(`Airline: ${carrier?.name || carrierFilter}`);
    }
    if (timeFilter !== "any") {
      const labels = { morning: "Morning (05-12)", afternoon: "Afternoon (12-17)",
                       evening: "Evening (17-21)", night: "Night (21-05)" };
      bits.push(`Departs ${labels[timeFilter] || timeFilter}`);
    }
    if (maxPrice && Number(maxPrice) > 0) bits.push(`Max ₹${Number(maxPrice).toLocaleString()}`);
    if (allianceFilter)                   bits.push(`${allianceFilter} Alliance only`);
    if (premiumHubsOnly)                  bits.push("Premium hubs only (DXB/LHR/DOH/SIN/CDG/JFK/HKG/etc.)");
    if (maxDurationH && Number(maxDurationH) > 0) bits.push(`Max ${maxDurationH}h journey`);
    if (sortBy !== "price_asc") {
      const sortLabels = { price_desc: "Highest fare first", duration_asc: "Shortest journey first", depart_asc: "Earliest departure first" };
      if (sortLabels[sortBy]) bits.push(`Sorted: ${sortLabels[sortBy]}`);
    }
    return bits.length ? `Member's active filters when picking this fare: ${bits.join(" · ")}.` : "";
  }

  return (
    <div
      data-testid="mobility-page-root"
      data-table-surface="mobility"
      style={{
        background: T.bg, minHeight: "100vh", color: T.ink,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass,
        "--t-verified": "#2f8a52",
      }}
    >
      <MobilityTopBar />
      <MemberContextBand pillar="mobility" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 28px 64px" }}>
{/* Hero */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 11, letterSpacing: "0.22em",
            color: T.brass, fontWeight: 600, textTransform: "uppercase",
            marginBottom: 12,
          }}>Concierge® · Mobility</div>
          <h1 className="t-serif" style={{
            fontSize: "clamp(40px, 5vw, 60px)", fontWeight: 400,
            lineHeight: 1.04, letterSpacing: "-0.02em", margin: 0,
          }}>
            Where will the journey <span style={{ color: T.brass, fontStyle: "italic" }}>take you?</span>
          </h1>
          <p style={{ color: T.ink2, fontSize: 16, marginTop: 14, maxWidth: 640, marginLeft: "auto", marginRight: "auto", lineHeight: 1.55 }}>
            Live flight fares from the world&apos;s carriers · or hand the brief to your concierge for private jets, yachts, cruises, rail, chauffeured cars, and sea transfers.
          </p>
        </div>

        {/* Search bar — flex-wraps on narrow viewports so the
            sidebar / mobile preview never clips fields or the CTA. */}
        <div style={{
          background: T.card,
          border: `1px solid ${T.line2}`,
          borderRadius: 18,
          padding: 18,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "flex-end",
        }} data-testid="mobility-flight-search-bar">
          <div style={{ flex: "1 1 150px", minWidth: 130 }}>
            <Field label="From" testid="mobility-origin">
              <input type="text" value={origin} onChange={(e) => setOrigin(e.target.value)}
                placeholder="Mumbai or BOM" style={inputStyle} data-testid="mobility-origin-input" />
            </Field>
          </div>
          <div style={{ flex: "1 1 150px", minWidth: 130 }}>
            <Field label="To" testid="mobility-destination">
              <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)}
                placeholder="Dubai or DXB" style={inputStyle} data-testid="mobility-destination-input" />
            </Field>
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <Field label="Depart" testid="mobility-depart">
              <input type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)}
                min={TODAY_ISO}
                style={inputStyle} data-testid="mobility-depart-input" />
            </Field>
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <Field label="Return (optional)" testid="mobility-return">
              <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                min={departDate || TODAY_ISO}
                style={inputStyle} data-testid="mobility-return-input" />
            </Field>
          </div>
          <div style={{ flex: "0 1 90px", minWidth: 80 }}>
            <Field label="Adults" testid="mobility-adults">
              <input type="number" min={1} max={9} value={adults} onChange={(e) => setAdults(Number(e.target.value || 1))}
                style={inputStyle} data-testid="mobility-adults-input" />
            </Field>
          </div>
          <div style={{ flex: "1 1 120px", minWidth: 110 }}>
            <Field label="Class" testid="mobility-cabin">
              <select value={cabin} onChange={(e) => setCabin(e.target.value)} style={inputStyle} data-testid="mobility-cabin-input">
                <option value="ECONOMY">Economy</option>
                <option value="PREMIUM_ECONOMY">Premium Economy</option>
                <option value="BUSINESS">Business</option>
                <option value="FIRST">First</option>
              </select>
            </Field>
          </div>
          <button onClick={searchFlights} disabled={loading || !origin || !destination || !departDate}
            data-testid="mobility-search-btn"
            style={{
              background: T.ink, color: "#FCFAF4",
              border: "none", borderRadius: 10,
              padding: "12px 18px",
              fontFamily: "inherit", fontSize: 14, fontWeight: 500,
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.6 : 1,
              flex: "1 1 140px", minWidth: 130,
              whiteSpace: "nowrap",
            }}>{loading ? "Searching…" : "Search flights"}</button>
        </div>

        {/* Filter + sort row (only when we have results) */}
        {searched && offers.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            marginTop: 18, padding: "12px 14px",
            background: "rgba(160,122,56,0.05)",
            border: `1px solid ${T.line}`,
            borderRadius: 12,
          }} data-testid="mobility-filter-row">
            {/* row 1 · stops + airlines */}
            <span style={filterLabelStyle}>Stops</span>
            <FilterChip active={stopsFilter === "any"}     onClick={() => setStopsFilter("any")}     label="Any" testid="filter-any" />
            <FilterChip active={stopsFilter === "nonstop"} onClick={() => setStopsFilter("nonstop")} label="Non-stop only" testid="filter-nonstop" />
            <div style={{ width: 1, height: 20, background: T.line2, margin: "0 4px" }} />
            <span style={filterLabelStyle}>Airline</span>
            <FilterChip active={!carrierFilter} onClick={() => setCarrierFilter(null)} label="All" testid="filter-carrier-all" />
            {carrierChips.map(c => (
              <FilterChip key={c.code}
                active={carrierFilter === c.code}
                onClick={() => setCarrierFilter(c.code === carrierFilter ? null : c.code)}
                label={c.name}
                testid={`filter-carrier-${c.code}`} />
            ))}

            {/* row 2 · time of day + max price + sort */}
            <div style={{ flexBasis: "100%", height: 0 }} />
            <span style={filterLabelStyle}>Departs</span>
            <FilterChip active={timeFilter === "any"}        onClick={() => setTimeFilter("any")}        label="Anytime"            testid="filter-time-any" />
            <FilterChip active={timeFilter === "morning"}    onClick={() => setTimeFilter("morning")}    label="Morning · 05-12"    testid="filter-time-morning" />
            <FilterChip active={timeFilter === "afternoon"}  onClick={() => setTimeFilter("afternoon")}  label="Afternoon · 12-17"  testid="filter-time-afternoon" />
            <FilterChip active={timeFilter === "evening"}    onClick={() => setTimeFilter("evening")}    label="Evening · 17-21"    testid="filter-time-evening" />
            <FilterChip active={timeFilter === "night"}      onClick={() => setTimeFilter("night")}      label="Night · 21-05"      testid="filter-time-night" />
            <div style={{ width: 1, height: 20, background: T.line2, margin: "0 4px" }} />
            <span style={filterLabelStyle}>Max INR</span>
            <input
              type="number" min={0} step={1000}
              value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="any"
              data-testid="filter-max-price"
              style={{
                width: 90, padding: "5px 10px",
                border: `1px solid ${T.line2}`, borderRadius: 999,
                background: "transparent", fontSize: 12, fontFamily: "inherit", color: T.ink,
                outline: "none",
              }} />
            <span style={{ marginLeft: "auto", ...filterLabelStyle }}>Sort</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              data-testid="mobility-sort-select"
              style={{ ...inputStyle, width: "auto", minWidth: 160, padding: "5px 10px", fontSize: 13, borderRadius: 999, border: `1px solid ${T.line2}` }}>
              <option value="price_asc">Lowest fare</option>
              <option value="price_desc">Highest fare</option>
              <option value="duration_asc">Shortest journey</option>
              <option value="depart_asc">Earliest departure</option>
            </select>

            {/* row 3 · alliance + premium hubs + max duration */}
            <div style={{ flexBasis: "100%", height: 0 }} />
            <span style={filterLabelStyle}>Alliance</span>
            <FilterChip active={!allianceFilter}            onClick={() => setAllianceFilter(null)}      label="Any"      testid="filter-alliance-any" />
            <FilterChip active={allianceFilter === "Star"}  onClick={() => setAllianceFilter("Star")}    label="Star"     testid="filter-alliance-star" />
            <FilterChip active={allianceFilter === "Oneworld"} onClick={() => setAllianceFilter("Oneworld")} label="Oneworld" testid="filter-alliance-oneworld" />
            <FilterChip active={allianceFilter === "SkyTeam"}  onClick={() => setAllianceFilter("SkyTeam")}  label="SkyTeam"  testid="filter-alliance-skyteam" />
            <div style={{ width: 1, height: 20, background: T.line2, margin: "0 4px" }} />
            <FilterChip
              active={premiumHubsOnly}
              onClick={() => setPremiumHubsOnly(!premiumHubsOnly)}
              label="Premium hubs only ✦"
              testid="filter-premium-hubs"
            />
            <div style={{ width: 1, height: 20, background: T.line2, margin: "0 4px" }} />
            <span style={filterLabelStyle}>Max hrs</span>
            <input
              type="number" min={0} step={1}
              value={maxDurationH} onChange={(e) => setMaxDurationH(e.target.value)}
              placeholder="any"
              data-testid="filter-max-duration"
              style={{
                width: 70, padding: "5px 10px",
                border: `1px solid ${T.line2}`, borderRadius: 999,
                background: "transparent", fontSize: 12, fontFamily: "inherit", color: T.ink,
                outline: "none",
              }} />
          </div>
        )}

        {/* Results */}
        {searched && (
          <section style={{ marginTop: 18 }} data-testid="mobility-flight-results">
            {filteredOffers.length === 0 && offers.length === 0 ? (
              <EmptyFlights origin={origin} destination={destination} departDate={departDate}
                onBrief={() => {
                  const filterLine = activeFilterSummary();
                  setActiveBrief({
                    service: "hunt",
                    label: "Flight Hunt",
                    prefill: {
                      origin, destination, depart_date: departDate, return_date: returnDate, adults, cabin,
                      notes_prefill: filterLine || "Amadeus returned no fares · please hunt this route.",
                    },
                  });
                }} />
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                  <h2 className="t-serif" style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
                    {filteredOffers.length} fare{filteredOffers.length === 1 ? "" : "s"}
                    <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: T.ink3, marginLeft: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {origin.toUpperCase()} → {destination.toUpperCase()}
                    </span>
                  </h2>
                  {gflights && (
                    <a href={gflights} target="_blank" rel="noreferrer"
                      data-testid="mobility-gflights-link"
                      style={{
                        fontSize: 13, color: T.brass, textDecoration: "none",
                        border: `1px solid ${T.brass}`, borderRadius: 999,
                        padding: "6px 14px",
                      }}>Compare fares →</a>
                  )}
                </div>

                {filteredOffers.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", color: T.ink3, fontSize: 14 }}>
                    No fares match the current filters. Try clearing one.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {filteredOffers.map((o, idx) => (
                      <FlightCard key={idx} o={o} onBookConcierge={() => {
                        const filterLine = activeFilterSummary();
                        const legs = (o.legs && o.legs.length > 0) ? o.legs : [o];
                        const legSummary = legs.map((l, li) => {
                          const tag = legs.length > 1 ? (li === 0 ? "Outbound: " : "Return: ") : "Selected fare: ";
                          return (
                            `${tag}${l.depart_iata}→${l.arrive_iata} ` +
                            `${fmtTime(l.depart_at)}–${fmtTime(l.arrive_at)} ` +
                            `· ${(l.carrier_names || []).join(", ") || (l.carriers || []).join(", ")}` +
                            (l.flight_numbers?.length ? ` · ${l.flight_numbers.join(" / ")}` : "") +
                            (l.aircraft?.length ? ` · ${l.aircraft.join(", ")}` : "")
                          );
                        }).join("\n");
                        const tail = ` · ${o.currency} ${o.total}` +
                          (o.fare_brand ? ` · ${o.fare_brand}` : "") +
                          (o.refundable === false ? " · Non-refundable" : (o.refundable ? " · Refundable" : ""));
                        const fareBlock = legSummary + tail;
                        setActiveBrief({
                          service: "flight",
                          label: "Concierge® ticketing",
                          prefill: {
                            origin, destination,
                            depart_date: departDate, return_date: returnDate,
                            adults, cabin,
                            trip_type: returnDate ? "round_trip" : "one_way",
                            notes_prefill: filterLine ? `${filterLine}\n\n${fareBlock}` : fareBlock,
                          },
                        });
                      }} />
                    ))}
                  </div>
                )}

                <p style={{ fontSize: 12, color: T.ink3, marginTop: 16, lineHeight: 1.5, fontStyle: "italic" }}
                  data-testid="mobility-pricing-disclaimer">
                  {disclaimer}
                </p>
              </>
            )}
          </section>
        )}

        {/* 6-tile dashboard */}
        <section style={{ marginTop: 56 }} data-testid="mobility-tile-grid">
          <div style={{ marginBottom: 10 }}>
            <span style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 11, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
            }}>Beyond the airline</span>
          </div>
          <h2 className="t-serif" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 24px", lineHeight: 1.2 }}>
            How else can we move you?
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}>
            {TILES.map((t) => (
              <button
                key={t.code}
                type="button"
                data-testid={`mobility-tile-${t.code}`}
                onClick={() => setActiveBrief({ service: t.code, label: t.label, prefill: {} })}
                style={{
                  background: `linear-gradient(180deg, rgba(33,28,21,0.10) 0%, rgba(33,28,21,0.78) 100%), url(${t.photo}) center/cover no-repeat`,
                  border: "none", borderRadius: 16,
                  padding: "22px 24px", textAlign: "left", cursor: "pointer",
                  fontFamily: "inherit", transition: "all 0.15s",
                  display: "flex", flexDirection: "column",
                  minHeight: 230,
                  color: "#fff", justifyContent: "flex-end",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 28px rgba(33,28,21,0.22)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <h3 className="t-serif" style={{ fontSize: 22, fontWeight: 500, margin: 0, textShadow: "0 1px 8px rgba(0,0,0,0.50)" }}>
                  {t.label}
                </h3>
                <p style={{ fontSize: 13, margin: "6px 0 12px", lineHeight: 1.5, color: "rgba(255,255,255,0.88)", textShadow: "0 1px 6px rgba(0,0,0,0.50)" }}
                  dangerouslySetInnerHTML={{ __html: t.blurb }} />
                <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "#FCFAF4", opacity: 0.95 }}>
                  Send a brief →
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>

      {activeBrief && (
        <BriefModal brief={activeBrief} onClose={() => setActiveBrief(null)} />
      )}

      <TableFooter slug="mobility" />
    </div>
  );
}


// ─── Header (mirrors AirportTopBar pattern exactly) ───────────────
function MobilityTopBar() {
  return (
    <header
      data-testid="mobility-header"
      style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        position: "sticky", top: 0, zIndex: 20,
      }}
    >
      <div style={{
        maxWidth: 1180, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 22px", gap: 10, flexWrap: "nowrap", minWidth: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <Link to="/" data-testid="mobility-home-button"
            style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>
              /mobility
            </span>
            <span className="lc-mobile-hide-on-narrow"
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: T.ink3, whiteSpace: "nowrap" }}>
              by Concierge®
            </span>
          </Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <ConciergeOnDutyPill context="mobility" />
          <CrossSurfaceNav testidPrefix="mobility-header-nav" size="compact" />
          <ConciergeSurfaceMenu testid="mobility-surface-menu" />
        </div>
      </div>
    </header>
  );
}


// ─── Small helpers ────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  background: "transparent",
  border: `1px solid ${T.line}`,
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 14,
  color: T.ink,
  fontFamily: "inherit",
  outline: "none",
};

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: T.ink3, letterSpacing: "0.14em", textTransform: "uppercase" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const filterLabelStyle = {
  fontSize: 11, color: T.ink3,
  fontFamily: "ui-monospace, monospace",
  letterSpacing: "0.12em", textTransform: "uppercase",
  marginRight: 2,
};

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      style={{
        background: active ? T.ink : "transparent",
        color: active ? "#FCFAF4" : T.ink2,
        border: `1px solid ${active ? T.ink : T.line2}`,
        borderRadius: 999, padding: "5px 12px",
        fontSize: 12, fontFamily: "inherit", cursor: "pointer",
        transition: "all 0.12s",
      }}>{label}</button>
  );
}

function fmtDuration(iso) {
  if (!iso) return "";
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return iso;
  return `${m[1] || "0"}h ${m[2] || "0"}m`;
}
function durationToMin(iso) {
  if (!iso) return 0;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return 0;
  return Number(m[1] || 0) * 60 + Number(m[2] || 0);
}
function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return ""; }
}

function FlightCard({ o, onBookConcierge }) {
  const legs = (o.legs && o.legs.length > 0) ? o.legs : [{
    depart_at: o.depart_at, arrive_at: o.arrive_at,
    depart_iata: o.depart_iata, arrive_iata: o.arrive_iata,
    depart_terminal: o.depart_terminal, arrive_terminal: o.arrive_terminal,
    duration: o.duration, stops: o.stops, layovers: o.layovers,
    aircraft: o.aircraft, flight_numbers: o.flight_numbers,
    carrier_names: o.carrier_names, carriers: o.carriers,
    codeshares: o.codeshares,
  }];
  const isRT = legs.length > 1;
  const seatsLow = o.number_of_bookable_seats && o.number_of_bookable_seats <= 4;
  const bag      = o.checked_bags;
  const cbag     = o.cabin_bags;

  // Baggage line
  let baggageLine = "";
  const hasChecked = bag && (bag.quantity > 0 || (bag.weight && bag.weight > 0));
  if (hasChecked) {
    if (bag.weight && bag.weight > 0) {
      const qty = bag.quantity > 0 ? `${bag.quantity}× ` : "1× ";
      baggageLine = `${qty}${bag.weight}${(bag.weight_unit || "kg").toLowerCase()} checked`;
    } else {
      baggageLine = `${bag.quantity}× checked`;
    }
  } else if (bag || cbag) {
    // Only claim "Cabin only" when the feed actually told us baggage
    // terms. Live-market offers ship null — honest silence, no badge.
    baggageLine = "Cabin only";
  }
  if (cbag && (cbag.quantity > 0 || (cbag.weight && cbag.weight > 0))) {
    const qty = cbag.quantity > 0 ? `${cbag.quantity}× ` : "";
    const wt  = cbag.weight ? `${cbag.weight}${(cbag.weight_unit || "kg").toLowerCase()}` : "";
    baggageLine += ` · ${qty}${wt} cabin`;
  }

  return (
    <div data-testid="mobility-flight-card" style={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: 14,
      padding: "16px 20px",
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 18,
      alignItems: "center",
    }}>
      <div style={{ minWidth: 0 }}>
        {legs.map((leg, idx) => (
          <LegBlock key={idx} leg={leg} idx={idx} isRT={isRT}
            cabin={o.cabin} alliances={idx === 0 ? (o.alliances || []) : []} />
        ))}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {o.fare_brand && <Pill color={T.brass}>{o.fare_brand}</Pill>}
          {baggageLine && <Pill color={T.ink2}>🧳 {baggageLine}</Pill>}
          {o.refundable === true && <Pill color="#2f8a52">Refundable</Pill>}
          {o.refundable === false && <Pill color={T.ink3}>Non-refundable</Pill>}
          {seatsLow && (
            <Pill color="#a04545">Only {o.number_of_bookable_seats} seat{o.number_of_bookable_seats > 1 ? "s" : ""} left</Pill>
          )}
          {isRT && <Pill color={T.brass}>Round-trip</Pill>}
        </div>
      </div>
      <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <div className="t-serif" style={{ fontSize: 22, fontWeight: 500, color: T.brass }}>
          {o.currency} {Number(o.total).toLocaleString()}
        </div>
        {isRT && <div style={{ fontSize: 10, color: T.ink3, fontFamily: "ui-monospace, monospace" }}>Round-trip total</div>}
        {o.last_ticketing_date && (
          <div style={{ fontSize: 10, color: T.ink3, fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
            Book by {o.last_ticketing_date}
          </div>
        )}
        <button onClick={onBookConcierge}
          data-testid="mobility-flight-concierge-btn"
          style={{
            marginTop: 6, background: "transparent",
            border: `1px solid ${T.brass}`, borderRadius: 999,
            color: T.brass, padding: "6px 14px",
            fontSize: 12, fontFamily: "inherit", fontWeight: 500, cursor: "pointer",
          }}>Concierge® handles →</button>
      </div>
    </div>
  );
}

function LegBlock({ leg, idx, isRT, cabin, alliances }) {
  const aircraftLine = (leg.aircraft || []).filter(Boolean).join(" · ");
  const flightNos    = (leg.flight_numbers || []).join(" · ");
  let stopsLine = "Non-stop";
  if (leg.stops > 0) {
    const lvs = (leg.layovers || []).map(l => {
      const h = Math.floor(l.wait_minutes / 60), m = l.wait_minutes % 60;
      return `${h}h${m ? ` ${m}m` : ""} at ${l.iata}`;
    }).join(" · ");
    stopsLine = `${leg.stops} stop${leg.stops > 1 ? "s" : ""}${lvs ? " · " + lvs : ""}`;
  }
  return (
    <div style={{
      marginTop: idx === 0 ? 0 : 10,
      paddingTop: idx === 0 ? 0 : 10,
      borderTop:  idx === 0 ? "none" : `1px dashed ${T.line}`,
    }}>
      {isRT && (
        <div style={{
          fontFamily: "ui-monospace, monospace", fontSize: 9,
          color: T.brass, letterSpacing: "0.20em",
          textTransform: "uppercase", fontWeight: 700, marginBottom: 4,
        }}>
          {idx === 0 ? "Outbound" : "Return"}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 2, flexWrap: "wrap" }}>
        <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, color: T.ink }}>
          {fmtTime(leg.depart_at)} → {fmtTime(leg.arrive_at)}
        </span>
        <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: T.ink3, letterSpacing: "0.10em" }}>
          {leg.depart_iata} → {leg.arrive_iata}
          {leg.depart_terminal && <> · T{leg.depart_terminal}</>}
          {leg.arrive_terminal && <> → T{leg.arrive_terminal}</>}
        </span>
        {alliances.map(a => (
          <span key={a} style={{
            fontSize: 10, fontFamily: "ui-monospace, monospace",
            color: T.brass, background: "rgba(160,122,56,0.08)",
            border: `1px solid ${T.brass}`, borderRadius: 999,
            padding: "1px 8px", letterSpacing: "0.10em", textTransform: "uppercase",
          }}>{a}</span>
        ))}
      </div>
      <div style={{ fontSize: 13, color: T.ink2, marginBottom: 2 }}>
        {fmtDuration(leg.duration)} ·{" "}
        {leg.stops === 0
          ? <span style={{ color: T.brass, fontWeight: 600 }}>Non-stop</span>
          : <span>{stopsLine}</span>}
        {" · "}
        <strong style={{ color: T.ink }}>{(leg.carrier_names || []).join(", ") || (leg.carriers || []).join(", ")}</strong>
        {flightNos && <> · {flightNos}</>}
        {idx === 0 && cabin && <> · <span style={{ textTransform: "capitalize" }}>{cabin.toLowerCase().replace("_", " ")}</span></>}
      </div>
      {aircraftLine && (
        <div style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}>
          {aircraftLine}
        </div>
      )}
      {(leg.codeshares || []).length > 0 && (
        <div style={{ fontSize: 11, color: T.ink3, fontStyle: "italic", marginTop: 2 }}>
          {leg.codeshares.map(cs => `${cs.segment}: marketed by ${cs.marketing_name}, operated by ${cs.operating_name}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function Pill({ color, children, title }) {
  return (
    <span title={title} style={{
      fontSize: 10, fontFamily: "ui-monospace, monospace",
      color, background: "transparent",
      border: `1px solid ${color}`, borderRadius: 999,
      padding: "2px 9px", letterSpacing: "0.04em",
    }}>{children}</span>
  );
}

function EmptyFlights({ origin, destination, departDate, onBrief }) {
  return (
    <div style={{
      background: T.card, border: `1px dashed ${T.line2}`,
      borderRadius: 16, padding: "32px 28px", textAlign: "center",
    }} data-testid="mobility-empty-flights">
      <h3 className="t-serif" style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
        No published fares on this route
      </h3>
      <p style={{ color: T.ink2, fontSize: 14, marginTop: 8, marginBottom: 18, lineHeight: 1.5 }}>
        {origin} → {destination} on {departDate}.<br />
        Some routes — last-minute, small carriers, charter — aren&apos;t on the public feeds.
        Let our desk hunt it down.
      </p>
      <button onClick={onBrief} data-testid="mobility-empty-brief-btn"
        style={{
          background: T.brass, color: "#FCFAF4",
          border: "none", borderRadius: 999, padding: "10px 22px",
          fontSize: 14, fontFamily: "inherit", fontWeight: 500, cursor: "pointer",
        }}>Send a flight-hunt brief →</button>
    </div>
  );
}


// ─── Per-tile brief field schemas ───────────────────────────────────
// Defines which structured fields each service's brief modal should ask.
const FIELD_SCHEMAS = {
  flight:     [["origin","From"],["destination","To"],["depart_date","Depart"],["return_date","Return (optional)"],["adults","Adults"],["cabin","Class"]],
  hunt:       [["origin","From"],["destination","To"],["depart_date","Depart"],["return_date","Return (optional)"],["adults","Adults"],["cabin","Class"]],
  aviation:   [["origin","From"],["destination","To"],["depart_date","Date"],["depart_time","Time"],["adults","Passengers"],["aviation_type","Jet or helicopter?"]],
  yacht:      [["city","Port / Marina"],["country","Country"],["start_date","Start date"],["duration_days","Duration (days)"],["adults","Guests"],["charter_type","Day or weekly?"]],
  cruise:     [["city","Cruise port (e.g. Barcelona)"],["start_date","Start date"],["duration_days","Duration (days)"],["adults","Adults"],["children","Children"],["cabin","Cabin preference"]],
  rail:       [["route","Route (e.g. Belmond Andean)"],["start_date","Date"],["adults","Adults"],["cabin","Cabin preference"]],
  chauffeured:[["city","City"],["start_date","Pickup date"],["end_date","Drop date"],["adults","Passengers"],["vehicle_pref","Vehicle preference"]],
  sea_transfer:[["origin","From"],["destination","To"],["start_date","Date"],["depart_time","Time"],["adults","Passengers"]],
};

const FIELD_TYPES = {
  origin: "text", destination: "text", route: "text",
  city: "text", country: "text",
  depart_date: "date", return_date: "date", start_date: "date", end_date: "date",
  depart_time: "time",
  adults: "number", children: "number", duration_days: "number",
  cabin: "text",
  aviation_type: "text", charter_type: "text", vehicle_pref: "text",
};


// ─── Brief modal (per-service fields + Viator inventory for cruise/chauffeured) ──
function BriefModal({ brief, onClose }) {
  const [fields, setFields] = useState(() => {
    const f = { ...(brief.prefill || {}) };
    if (!f.adults) f.adults = 2;
    if (!f.children) f.children = 0;
    // notes_prefill is a UX-only field — strip it out of the structured
    // payload so it doesn't reach the backend as a phantom field.
    delete f.notes_prefill;
    return f;
  });
  const embedMember = useEmbedMember();
  const [name,  setName]  = useState(embedMember?.name  || "");
  const [email, setEmail] = useState(embedMember?.email || "");
  const [phone, setPhone] = useState(embedMember?.phone || "");
  const [notes, setNotes] = useState(brief.prefill?.notes_prefill || "");
  const [splitCarriers, setSplitCarriers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [response,   setResponse]   = useState(null);

  // Viator inventory for cruise / chauffeured tiles
  const [viatorProducts, setViatorProducts] = useState([]);
  const [viatorLoading,  setViatorLoading]  = useState(false);

  const schema = FIELD_SCHEMAS[brief.service] || [];
  const showsViator = brief.service === "cruise" || brief.service === "chauffeured";
  const viatorQuery = brief.service === "cruise"
    ? (fields.city || "")
    : (brief.service === "chauffeured" ? (fields.city || "") : "");

  // Trigger Viator search when the city/port input has 3+ chars
  useEffect(() => {
    if (!showsViator || !viatorQuery || viatorQuery.length < 3) {
      setViatorProducts([]);
      return;
    }
    const handle = setTimeout(async () => {
      setViatorLoading(true);
      try {
        const ep = brief.service === "cruise" ? "cruise/shore_excursions" : "chauffeured/drivers";
        const r = await fetch(`${API}/api/mobility/${ep}?${brief.service === "cruise" ? "port" : "city"}=${encodeURIComponent(viatorQuery)}&limit=8`);
        const j = await r.json();
        setViatorProducts(j.products || []);
      } catch {
        setViatorProducts([]);
      } finally {
        setViatorLoading(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [brief.service, viatorQuery, showsViator]);

  function setField(k, v) {
    setFields((p) => ({ ...p, [k]: v }));
  }

  // Only flight briefs with a return date are true round-trips and
  // therefore eligible for the "split carriers" option.
  const splitEligible = brief.service === "flight" && !!fields.return_date;

  async function submit() {
    setSubmitting(true);
    try {
      const splitNote = splitEligible && splitCarriers
        ? "Member is open to outbound & return on different carriers if it improves price or timing. "
        : "";
      const body = {
        service: brief.service,
        ...fields,
        split_carriers: splitEligible && splitCarriers ? "Yes" : "No",
        preferences: (splitNote + (notes || "")).trim(),
        member_name:  name  || null,
        member_email: email || null,
        member_phone: phone || null,
      };
      const r = await fetch(`${API}/api/mobility/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      setResponse(j);
    } catch (e) {
      setResponse({ ok: false, detail: "Could not reach the concierge. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(33,28,21,0.55)", backdropFilter: "blur(6px)",
        zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: 16, paddingTop: 40, overflowY: "auto",
      }}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="mobility-brief-modal"
        style={{
          background: T.card, color: T.ink,
          borderRadius: 18, padding: "28px 32px",
          maxWidth: 640, width: "100%",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        }}>
        {!response ? (
          <>
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 10, color: T.brass, letterSpacing: "0.20em", textTransform: "uppercase", fontWeight: 600 }}>
                Concierge® brief
              </span>
            </div>
            <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "0 0 6px" }}>
              {brief.label}
            </h2>
            <p style={{ color: T.ink2, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
              Tell us what you have in mind &mdash; your concierge replies within an hour.
            </p>

            {/* Per-service structured fields */}
            {schema.length > 0 && (
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
                marginBottom: 14,
              }}>
                {schema.map(([key, label]) => (
                  <Field key={key} label={label} testid={`brief-${key}`}>
                    <input
                      type={FIELD_TYPES[key] || "text"}
                      value={fields[key] ?? ""}
                      onChange={(e) => setField(key, FIELD_TYPES[key] === "number" ? Number(e.target.value || 0) : e.target.value)}
                      min={FIELD_TYPES[key] === "date" ? TODAY_ISO : undefined}
                      style={inputStyle}
                      data-testid={`brief-${key}-input`}
                    />
                  </Field>
                ))}
              </div>
            )}

            {/* Split carriers toggle · only for true round-trip flights.
                Lets the member tell the concierge they're open to
                outbound & return on different carriers for better price
                or timing. The boolean ships in the payload AND a
                human-readable note is prepended to `preferences` so
                the concierge sees it inline. */}
            {splitEligible && (
              <label
                data-testid="brief-split-carriers"
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  marginBottom: 14, padding: "10px 12px",
                  background: "rgba(160,122,56,0.06)",
                  border: `1px solid ${T.line}`, borderRadius: 10,
                  cursor: "pointer",
                }}>
                <input
                  type="checkbox"
                  checked={splitCarriers}
                  onChange={(e) => setSplitCarriers(e.target.checked)}
                  data-testid="brief-split-carriers-input"
                  style={{ marginTop: 3, accentColor: T.brass, cursor: "pointer" }}
                />
                <span style={{ fontSize: 13, color: T.ink, lineHeight: 1.5 }}>
                  <strong style={{ fontWeight: 500 }}>Split this trip.</strong>{" "}
                  <span style={{ color: T.ink2 }}>
                    I&apos;m open to outbound &amp; return on different carriers if it improves price or timing.
                  </span>
                </span>
              </label>
            )}

            {/* Viator inventory · only for cruise + chauffeured, only
                surfaces (with attribution) when we actually have live
                products to show — otherwise stays invisible so the
                form doesn't read like a 3rd-party widget. */}
            {showsViator && (viatorProducts.length > 0 || viatorLoading) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{
                  fontFamily: "ui-monospace, monospace", fontSize: 10,
                  color: T.brass, letterSpacing: "0.18em",
                  textTransform: "uppercase", fontWeight: 600, marginBottom: 8,
                }}>
                  {brief.service === "cruise" ? "Live shore excursions" : "Live private drivers"}
                </div>
                {viatorLoading && (
                  <p style={{ color: T.ink3, fontSize: 13, fontStyle: "italic" }}>Searching live inventory…</p>
                )}
                {viatorProducts.length > 0 && (
                  <>
                    <div style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
                      maxHeight: 260, overflowY: "auto",
                      padding: 8, background: T.bg, borderRadius: 10,
                      border: `1px solid ${T.line}`,
                    }} data-testid="brief-viator-products">
                      {viatorProducts.map((p, i) => (
                        <a key={i} href={p.book_url} target="_blank" rel="noreferrer"
                          data-testid={`viator-product-${i}`}
                          style={{
                            display: "block", textDecoration: "none", color: "inherit",
                            background: T.card, border: `1px solid ${T.line}`,
                            borderRadius: 8, padding: 8,
                            fontSize: 12, lineHeight: 1.4,
                          }}>
                          {p.hero_url && (
                            <img src={p.hero_url} alt="" style={{
                              width: "100%", height: 76, objectFit: "cover",
                              borderRadius: 6, marginBottom: 6,
                            }} />
                          )}
                          <div style={{ fontWeight: 500, color: T.ink, fontSize: 12, marginBottom: 2 }}>
                            {p.title?.length > 70 ? p.title.slice(0, 70) + "…" : p.title}
                          </div>
                          <div style={{ color: T.brass, fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                            from {p.currency} {p.from_price}
                            {p.rating && <> · ★ {p.rating}</>}
                          </div>
                        </a>
                      ))}
                    </div>
                    <p style={{ color: T.ink3, fontSize: 11, marginTop: 6, fontStyle: "italic" }}>
                      Powered by Viator · A Tripadvisor company.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Member contact + free-text */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Your name" testid="brief-name">
                <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="brief-name-input" />
              </Field>
              <Field label="Email" testid="brief-email">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} data-testid="brief-email-input" />
              </Field>
              <Field label="Phone (optional)" testid="brief-phone">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} data-testid="brief-phone-input" />
              </Field>
              <div />
            </div>
            <Field label="Anything else?" testid="brief-notes">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                placeholder="Brand or route preferences, special occasion, dietary needs, accessibility, etc."
                style={{ ...inputStyle, resize: "vertical", minHeight: 84, marginTop: 4 }}
                data-testid="brief-notes-input" />
            </Field>
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={onClose} style={{
                background: "transparent", color: T.ink2,
                border: `1px solid ${T.line2}`, borderRadius: 999,
                padding: "10px 18px", fontFamily: "inherit", fontSize: 13, cursor: "pointer",
              }} data-testid="brief-cancel-btn">Cancel</button>
              <button onClick={submit} disabled={submitting || !email}
                data-testid="brief-submit-btn"
                style={{
                  background: T.ink, color: "#FCFAF4",
                  border: "none", borderRadius: 999,
                  padding: "10px 22px",
                  fontFamily: "inherit", fontSize: 13, fontWeight: 500,
                  cursor: submitting ? "wait" : "pointer",
                  opacity: !email || submitting ? 0.6 : 1, marginLeft: "auto",
                }}>{submitting ? "Sending…" : "Send brief"}</button>
            </div>
          </>
        ) : (
          <div data-testid="mobility-brief-success">
            <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "0 0 8px" }}>
              {response.ok ? "Brief logged" : "Something went wrong"}
            </h2>
            <p style={{ color: T.ink2, fontSize: 14, lineHeight: 1.55 }}>
              {response.ok
                ? <>Your concierge has the brief. Reference: <code style={{ color: T.brass, fontSize: 13 }}>{response.display_ref}</code>. We&apos;ll be in touch within the hour.</>
                : (response.detail || "Please try again.")}
            </p>
            <button onClick={onClose}
              style={{
                marginTop: 18,
                background: T.ink, color: "#FCFAF4",
                border: "none", borderRadius: 999,
                padding: "10px 22px",
                fontFamily: "inherit", fontSize: 13, fontWeight: 500, cursor: "pointer",
              }} data-testid="brief-close-btn">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
